/* eslint-disable no-undef */
import { useContext, useRef } from 'react';
import { IconButton, Typography } from '@material-ui/core';
import DragIcon from '@material-ui/icons/DragIndicatorOutlined';
import { useDrag, useDrop } from 'react-dnd';
import { AdminTestContext } from '@orientaction/Context';
import { publicIconPath } from '@orientaction/utils';
import useStyles from './style';

interface RecommendedTest {
  id: number;
  title: string;
  description?: string;
  order: number;
}

interface IProps {
  test: RecommendedTest;
  index: number;
}

interface DragItem {
  index: number;
  id: number;
  type: string;
}

const ItemTypes = {
  RECOMMENDED_TEST: 'recommendedTest',
};

const RecommendedTestCard: React.FC<IProps> = ({ test, index }) => {
  const { test: testContext, updateTest } = useContext<any>(AdminTestContext);
  const classes = useStyles();
  const ref = useRef<HTMLDivElement>(null);

  const moveCard = (dragIndex: number, hoverIndex: number) => {
    const dragTest = testContext.recommendedTests[dragIndex];
    const newRecommendedTests = [...testContext.recommendedTests];
    newRecommendedTests.splice(dragIndex, 1);
    newRecommendedTests.splice(hoverIndex, 0, dragTest);

    const updatedTests = newRecommendedTests.map((t, idx) => ({
      ...t,
      order: idx,
    }));
    updateTest({ recommendedTests: updatedTests });
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    const newRecommendedTests = testContext.recommendedTests
      .filter((t: RecommendedTest) => t.id !== test.id)
      .map((t: RecommendedTest, idx: number) => ({ ...t, order: idx }));
    updateTest({ recommendedTests: newRecommendedTests });
  };

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: any }>({
    accept: ItemTypes.RECOMMENDED_TEST,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover(item, monitor) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      if (dragIndex === hoverIndex) {
        return;
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect();
      const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
      const clientOffset = monitor.getClientOffset();
      const hoverClientY = (clientOffset as any).y - hoverBoundingRect.top;

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      moveCard(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.RECOMMENDED_TEST,
    item: () => {
      return { id: test.id, index };
    },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0.4 : 1;
  drag(drop(ref));

  return (
    <div ref={ref} style={{ opacity }} data-handler-id={handlerId}>
      <div className={classes.flexContainer}>
        <IconButton className={classes.grab}>
          <DragIcon />
        </IconButton>
        <div className={classes.root}>
          <div className={classes.accordionSummary}>
            <div className={classes.accordionFlexContainer}>
              <div className={classes.summaryText}>
                <Typography className={classes.summaryTextH6} variant="h6">
                  {test.title}
                </Typography>
              </div>
              <div>
                <IconButton className={classes.iconButton} onClick={handleDelete}>
                  <img src={`${publicIconPath}/oa_trash.svg`} alt="Delete Icon" />
                </IconButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecommendedTestCard; 